import React, { useRef, useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Store, Utensils, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import type { CantinaTab } from '../../types';

export interface AdminCantinaTabsCardProps {
  tabs: CantinaTab[];
  recentPayments?: {tabId?: string, slotNumber?: number, amount: number, timestamp: number}[];
  onTabClick: (tabId: string, defaultName?: string) => void;
  onNewTabClick: (defaultName?: string, slotNumber?: number) => void;
  onFastSaleClick?: () => void;
}

export function AdminCantinaTabsCard({ tabs, recentPayments, onTabClick, onNewTabClick, onFastSaleClick }: AdminCantinaTabsCardProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasDragged = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const [showLeftGradient, setShowLeftGradient] = useState(false);

  // Calcular el casillero más alto ocupado
  const maxTabSlot = tabs.reduce((max, tab) => Math.max(max, tab.slotNumber || 0), 0);
  const [totalSlots, setTotalSlots] = useState(Math.max(10, maxTabSlot));

  // Expandir automáticamente si viene de la DB un casillero más alto
  useEffect(() => {
    if (maxTabSlot > totalSlots) {
      setTotalSlots(maxTabSlot);
    }
  }, [maxTabSlot, totalSlots]);

  const handleAddSlot = () => {
    setTotalSlots(prev => prev + 1);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          left: scrollRef.current.scrollWidth,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const handleRemoveSlot = () => {
    if (totalSlots > 10) {
      const lastSlotOccupied = tabs.some(t => t.slotNumber === totalSlots);
      if (!lastSlotOccupied) {
        setTotalSlots(prev => prev - 1);
      }
    }
  };

  // Check if we need to show the gradients/arrows
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowRightGradient(scrollLeft + clientWidth < scrollWidth - 5);
      setShowLeftGradient(scrollLeft > 5);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    hasDragged.current = false;
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    if (Math.abs(x - startX) > 5) {
      hasDragged.current = true;
    }
    const walk = (x - startX) * 2; // scroll-fast multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
    checkScroll();
  };

  const handleTabClickInternal = (tabId: string, defaultName?: string) => {
    if (hasDragged.current) return;
    onTabClick(tabId, defaultName);
  };

  const handleNewTabClickInternal = (defaultName: string, num: number) => {
    if (hasDragged.current) return;
    onNewTabClick(defaultName, num);
  };

  const handleScrollRight = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 80, behavior: 'smooth' });
      setTimeout(checkScroll, 300); // Check after smooth scroll animation
    }
  };

  const handleScrollLeft = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -80, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  return (
    <div className="col-span-8 md:col-span-7 bg-[#1a1c23] border border-[#2d313f] rounded-xl md:rounded-2xl p-1 md:p-2 h-[68px] md:h-[86px] relative shadow-lg flex items-center group/mesas">
      {/* Título integrado visualmente, fijo a la izquierda */}
      <div className="flex flex-col justify-center items-center h-full px-2 md:px-3 shrink-0 mr-1 z-20 cursor-default relative">
        <div className="text-[#3366cc] mb-0.5 flex flex-col items-center">
          <Store size={20} />
        </div>
        <span className="text-[10px] md:text-xs font-black uppercase tracking-wider text-white">Cantina</span>
      </div>

      {/* Indicador Izquierdo (Flecha y gradiente) */}
      <div 
        className={`absolute left-[55px] md:left-[75px] top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-[#1a1c23] via-[#1a1c23]/90 to-transparent pointer-events-none flex items-center justify-start pl-2 md:pl-3 transition-opacity duration-300 z-10 ${showLeftGradient ? 'opacity-100' : 'opacity-0'}`}
      >
        <button 
          onClick={handleScrollLeft}
          className="pointer-events-auto bg-[#1a1c23]/50 hover:bg-[#1a1c23] rounded-full p-1 backdrop-blur-sm border border-white/10 cursor-pointer transition-all hover:scale-110 active:scale-95"
        >
          <ChevronLeft size={28} className="text-white/80 drop-shadow-lg" />
        </button>
      </div>

      {/* Contenedor scrolleable sin scrollbar visible */}
      <div 
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onScroll={checkScroll}
        className={`cantina-hide-scroll flex items-center gap-1.5 md:gap-2 overflow-x-auto overflow-y-hidden flex-1 px-1 h-full ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`.cantina-hide-scroll::-webkit-scrollbar { display: none; }`}</style>
        
        {/* Renderizamos casilleros dinámicamente */}
        {Array.from({ length: totalSlots }).map((_, idx) => {
          const num = idx + 1;
          const existingTab = tabs.find(t => t.slotNumber === num || (!t.slotNumber && tabs.indexOf(t) === idx));
          
          const recentPayment = recentPayments?.find(p => p.slotNumber === num || (existingTab && p.tabId === existingTab.id));

          return (
            <AnimatePresence mode="popLayout" key={`slot-container-${num}`}>
              {recentPayment ? (
                <motion.div
                  key={`payment-${num}-${recentPayment.timestamp}`}
                  initial={{ scale: 0.5, opacity: 0, y: 10 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0, opacity: 0, transition: { duration: 0.2 } }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  className="h-[92%] my-auto aspect-square bg-[#8cc63f] border-2 border-[#a3d95b] rounded-lg md:rounded-xl flex flex-col items-center justify-center p-1 shrink-0 shadow-md z-20 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse mix-blend-overlay" />
                  
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.15, type: 'spring', stiffness: 400 }}
                  >
                    <CheckCircle2 size={24} className="text-white mb-0.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] relative z-10" />
                  </motion.div>

                  <span className="text-[9px] md:text-[10px] font-black text-white uppercase tracking-wider w-full text-center relative z-10 leading-none mt-1 [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">COBRADO</span>
                  <span className="text-[10px] md:text-[12px] font-black text-white mt-0.5 relative z-10 [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]">${recentPayment.amount.toLocaleString('es-AR')}</span>
                </motion.div>
              ) : existingTab ? (
                <motion.button 
                  key={existingTab.id || `tab-${num}`}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => handleTabClickInternal(existingTab.id!, existingTab.name || `Mesa ${num}`)}
                  className="h-[92%] my-auto aspect-square bg-[#3366cc] hover:bg-[#6699ff] hover:-translate-y-0.5 hover:shadow-[0_4px_15px_rgba(102,153,255,0.4)] z-0 hover:z-10 border border-[#3366cc] rounded-lg md:rounded-xl flex flex-col items-center justify-center p-1 transition-all shrink-0 shadow-lg shadow-[#3366cc]/30 active:scale-95 group select-none"
                >
                  <span className="text-[8px] md:text-[10px] font-black text-white uppercase tracking-wider truncate w-full text-center group-hover:scale-105 transition-transform">{existingTab.name ? existingTab.name : `Mesa ${num}`}</span>
                  <span className="text-[9px] md:text-[12px] font-black text-white mt-0.5">${existingTab.totalAmount.toLocaleString('es-AR')}</span>
                </motion.button>
              ) : (
                <motion.button 
                  key={`empty-${num}`} 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => handleNewTabClickInternal(`Mesa ${num}`, num)} 
                  className="h-[92%] my-auto aspect-square bg-black/20 hover:bg-white/10 border border-dashed border-[#2d313f] hover:border-zinc-400 hover:-translate-y-0.5 hover:shadow-[0_4px_15px_rgba(255,255,255,0.05)] z-0 hover:z-10 rounded-lg md:rounded-xl flex items-center justify-center transition-all shrink-0 group active:scale-95 cursor-pointer"
                >
                  <span className="text-sm md:text-xl font-black text-[#2d313f] group-hover:text-zinc-500">{num}</span>
                </motion.button>
              )}
            </AnimatePresence>
          );
        })}

        {/* Renderizamos las cuentas que tienen slotNumber > totalSlots (si las hubiera por algun bug anterior) */}
        {tabs.filter(t => t.slotNumber && t.slotNumber > totalSlots).map((tab) => (
          <button 
            key={tab.id}
            onClick={() => handleTabClickInternal(tab.id!, tab.name || `Mesa ${tab.slotNumber}`)}
            className="h-[92%] my-auto aspect-square bg-[#3366cc] hover:bg-[#6699ff] hover:-translate-y-0.5 hover:shadow-[0_4px_15px_rgba(102,153,255,0.4)] z-0 hover:z-10 border border-[#3366cc] rounded-lg md:rounded-xl flex flex-col items-center justify-center p-1 transition-all shrink-0 shadow-lg shadow-[#3366cc]/30 active:scale-95 group select-none"
          >
            <span className="text-[8px] md:text-[10px] font-black text-white uppercase tracking-wider truncate w-full text-center group-hover:scale-105 transition-transform">{tab.name ? tab.name : `Mesa ${tab.slotNumber}`}</span>
            <span className="text-[9px] md:text-[12px] font-black text-white mt-0.5">${tab.totalAmount.toLocaleString('es-AR')}</span>
          </button>
        ))}

        {/* Bloque de control +/- */}
        <div className="flex flex-col gap-1 h-[92%] my-auto mr-2 md:mr-4 shrink-0 w-[32px] md:w-[42px]">
          <button 
            className="flex-1 bg-theme-primary/10 hover:bg-white border border-theme-primary/30 hover:border-white rounded-lg md:rounded-xl flex items-center justify-center transition-all group active:scale-95 cursor-pointer" 
            onClick={handleAddSlot}
            title="Agregar casillero vacío"
          >
            <span className="text-xl md:text-2xl font-black text-theme-primary group-hover:text-black transition-all leading-none">+</span>
          </button>
          <button 
            className={`flex-1 border rounded-lg md:rounded-xl flex items-center justify-center transition-all
              ${totalSlots > 10 && !tabs.some(t => t.slotNumber === totalSlots) 
                ? 'bg-[#2d313f] hover:bg-[#3d4255] border-zinc-400 hover:border-zinc-300 group active:scale-95 cursor-pointer' 
                : 'bg-[#1a1c24] border-zinc-800/50 cursor-not-allowed opacity-50'}`}
            onClick={handleRemoveSlot}
            disabled={totalSlots <= 10 || tabs.some(t => t.slotNumber === totalSlots)}
            title={totalSlots <= 10 ? 'Mínimo 10 casilleros' : tabs.some(t => t.slotNumber === totalSlots) ? 'El último casillero está activo' : 'Eliminar último casillero'}
          >
            <span className={`text-xl md:text-2xl font-black leading-none transition-all inline-block scale-x-125
              ${totalSlots > 10 && !tabs.some(t => t.slotNumber === totalSlots)
                ? 'text-white group-hover:scale-x-[1.4] group-hover:scale-y-110'
                : 'text-zinc-600'}`}>-</span>
          </button>
        </div>
      </div>

      {/* Indicador Derecho (Flecha y gradiente) */}
      <div 
        className={`absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-[#1a1c23] via-[#1a1c23]/90 to-transparent pointer-events-none flex items-center justify-end pr-2 md:pr-3 rounded-r-xl md:rounded-r-2xl transition-opacity duration-300 z-10 ${showRightGradient ? 'opacity-100' : 'opacity-0'}`}
      >
        <button 
          onClick={handleScrollRight}
          className="pointer-events-auto bg-[#1a1c23]/50 hover:bg-[#1a1c23] rounded-full p-1 backdrop-blur-sm border border-white/10 cursor-pointer transition-all hover:scale-110 active:scale-95"
        >
          <ChevronRight size={28} className="text-white/80 animate-pulse drop-shadow-lg" />
        </button>
      </div>
    </div>
  );
}
